// UnsavedChangesProvider.tsx

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import HasUnsavedChangesDialog from '../components/HasUnsavedChangesDialog';
import { useRouteMatch } from 'react-router';
import history, { setNavigationConfirmation } from '../historyUtil';

interface UnsavedChangesContextValue {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  handleNavigation: (action: () => void) => void;
  confirmNavigation: () => void;
  cancelNavigation: () => void;
  isDialogOpen: boolean;
}

const UnsavedChangesContext = React.createContext<UnsavedChangesContextValue | null>(null);

export const UnsavedChangesProvider: React.FC = ({ children }) => {
  const match = useRouteMatch();
  const portfolioId = parseInt(match.params.portfolioID, 10);
  const location = useLocation();

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

  // Ref to track dialog state inside the synchronous block callback
  // (avoids stale closure issues with the state variable)
  const isDialogOpenRef = useRef(false);

  // Ref to the unblock function so confirmNavigation can remove the block
  // before replaying in-app navigation
  const unblockRef = useRef<(() => void) | null>(null);

  // getUserConfirmation callback for browser POP events: confirming calls it with
  // `true` to let the POP proceed (the browser already sits at the target).
  const popCallbackRef = useRef<((ok: boolean) => void) | null>(null);

  // Snapshot taken the moment a POP is intercepted: a background history.replace
  // (e.g. query-param update) can mutate history.location while the dialog is open.
  const popLocationRef = useRef<typeof history.location | null>(null);

  // If the route changes or portfolioId changes, reset hasUnsavedChanges
  useEffect(() => {
    setHasUnsavedChanges(false);
  }, [portfolioId, location.pathname]);

  // Never cosmetically revert the address bar via replaceState: that mutates history
  // entries outside history v4's control and desyncs allKeys after repeated cancels.
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    // Register the confirmation handler that history's getUserConfirmation
    // delegates to.  Called when the block callback returns a string.
    setNavigationConfirmation((_message, callback) => {
      // Rapid back presses each fire their own getUserConfirmation; overwriting the ref
      // orphans earlier callbacks — safe because history v4's confirmTransitionTo is stateless.
      popCallbackRef.current = callback;
      // Snapshot the page we're on now (setState hasn't run, so this is still the
      // edit page) so cancelNavigation can deterministically restore it.
      popLocationRef.current = history.location;

      if (!isDialogOpenRef.current) {
        setIsDialogOpen(true);
        isDialogOpenRef.current = true;
      }
    });

    const unblock = history.block((_targetLocation, action) => {
      // Only intercept POP (browser back/forward buttons).
      // PUSH and REPLACE from in-app code pass through unblocked.
      if (action !== 'POP') {
        return undefined;
      }

      // Must be a truthy string so history v4 routes through getUserConfirmation instead
      // of calling callback(result !== false) synchronously; the content itself is unused.
      return 'unsaved';
    });

    unblockRef.current = unblock;

    return () => {
      unblock();
      unblockRef.current = null;
      setNavigationConfirmation(null);
      // Reject any pending POP: an external setHasUnsavedChanges(false) while the dialog is
      // open would otherwise leave the browser at the target with history.location behind.
      if (popCallbackRef.current) {
        popCallbackRef.current(false);
      }
      popCallbackRef.current = null;
      popLocationRef.current = null;
      // Close the dialog if it was open when the effect cleaned up
      // (e.g. external setHasUnsavedChanges(false) while dialog is visible).
      if (isDialogOpenRef.current) {
        setIsDialogOpen(false);
        isDialogOpenRef.current = false;
      }
    };
  }, [hasUnsavedChanges]);

  const handleNavigation = useCallback(
    (action: () => void) => {
      if (hasUnsavedChanges) {
        setIsDialogOpen(true);
        isDialogOpenRef.current = true;
        setPendingNavigation(() => action);
      } else {
        action();
      }
    },
    [hasUnsavedChanges],
  );

  const confirmNavigation = useCallback(() => {
    try {
      if (pendingNavigation) {
        // Remove the block BEFORE running the navigation so pushes inside pendingNavigation
        // (and cascading ones, e.g. TeamProvider's listen → history.replace) aren't intercepted.
        if (unblockRef.current) {
          unblockRef.current();
          unblockRef.current = null;
        }
        pendingNavigation();
        setPendingNavigation(null);
        // Orphan any concurrent POP callback: cb(true) would move history.location to the
        // POP target, so pendingNavigation() would build its URL off the wrong pathname.
        popCallbackRef.current = null;
      } else if (popCallbackRef.current) {
        // The browser already sits at the target; cb(true) syncs history.location + Router.
        popCallbackRef.current(true);
        popCallbackRef.current = null;
        popLocationRef.current = null;
      }
    } finally {
      setHasUnsavedChanges(false);
      setIsDialogOpen(false);
      isDialogOpenRef.current = false;
    }
  }, [pendingNavigation]);

  const cancelNavigation = useCallback(() => {
    if (popCallbackRef.current) {
      // Don't reject via cb(false): revertPop's go(delta) is a no-op when the back-target
      // predates this JS session (in-memory allKeys), so push this page back instead.
      const loc = popLocationRef.current ?? history.location;
      popCallbackRef.current = null;
      popLocationRef.current = null;
      history.push({ pathname: loc.pathname, search: loc.search, hash: loc.hash, state: loc.state });
    }
    setIsDialogOpen(false);
    isDialogOpenRef.current = false;
    setPendingNavigation(null);
  }, []);

  return (
    <UnsavedChangesContext.Provider
      value={{
        hasUnsavedChanges,
        setHasUnsavedChanges,
        handleNavigation,
        confirmNavigation,
        cancelNavigation,
        isDialogOpen,
      }}
    >
      {children}
      <HasUnsavedChangesDialog
        open={isDialogOpen}
        cancelNavigation={cancelNavigation}
        confirmNavigation={confirmNavigation}
      />
    </UnsavedChangesContext.Provider>
  );
};

export const useUnsavedChanges = (): UnsavedChangesContextValue => {
  const context = React.useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error('useUnsavedChanges must be used within an UnsavedChangesProvider');
  }
  return context;
};
